/** @jsxImportSource @emotion/react */
import css, { SerializedStyles } from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { Select, Spin } from 'antd'
import React, { useEffect, useMemo } from 'react'
import { paragraphMedium, weightRegular } from 'src/components/css'
import { ErrorLabel } from '../ErrorLabel'
import Text from '../Text'
import { useCountries } from '@sportninja/common/hooks/useCountries'

/**
 * CountrySelectInput component for selecting countries with search functionality
 * @component
 */
type CountrySelectInputProps = {
  /** Field name for error handling and identification */
  name: string
  /** Current selected country value (iso_3166_2 code) */
  value: string | undefined
  /** Function to update the selected country value */
  onChange: (value: string | undefined) => void
  /** Object containing field validation errors */
  fieldErrors?: any
  /** Custom error messages */
  customError?: any
  /** Label text displayed above the select */
  label?: string
  /** Placeholder text */
  placeholder?: string
  /** Whether to allow clearing the selection */
  allowClear?: boolean
  /** Whether the select is disabled */
  disabled?: boolean
  /** Whether to show search functionality */
  showSearch?: boolean
  /** Tab index for keyboard navigation */
  tabIndex?: number
  /** Custom CSS styles using emotion css */
  style?: SerializedStyles
  /** Whether the field is required (shows asterisk after label) */
  required?: boolean
}

const filterOption = (input: string, option: any) =>
  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())

export const CountrySelectInput: React.FC<CountrySelectInputProps> = ({
  name,
  value,
  onChange,
  fieldErrors = {},
  customError = {},
  label = 'Country',
  placeholder = 'Select a country',
  allowClear = false,
  disabled = false,
  showSearch = true,
  tabIndex,
  style,
  required = false,
}) => {
  console.log('🗺️ CountrySelectInput: initializing component')

  const { countries, loading, error, refetch } = useCountries()

  // Fetch countries on mount if not already loaded
  useEffect(() => {
    if (countries.length === 0) {
      console.log('🗺️ CountrySelectInput: fetching countries')
      refetch()
    }
  }, [countries.length, refetch])

  // Log when countries are loaded
  useEffect(() => {
    if (countries.length > 0) {
      console.log(
        '🗺️ CountrySelectInput: loaded',
        countries.length,
        'countries'
      )
    }
  }, [countries.length])

  // Log errors
  useEffect(() => {
    if (error) {
      console.error('🗺️ CountrySelectInput: fetch error', error)
    }
  }, [error])

  // Build options array with memoization
  const countryOptions = useMemo(() => {
    return countries.map((country) => ({
      label: country.name,
      value: country.iso_3166_2,
    }))
  }, [countries])

  // Handle change to validate that only valid country codes are accepted
  const handleChange = (newValue: string | undefined) => {
    console.log('🗺️ CountrySelectInput: handleChange called with:', newValue)

    // If clearing the selection
    if (newValue === undefined || newValue === null) {
      onChange(undefined)
      return
    }

    // Check if the value is a valid country code
    const isValidCountry = countries.some(
      (country) => country.iso_3166_2 === newValue
    )

    if (isValidCountry) {
      console.log('🗺️ CountrySelectInput: Valid country selected:', newValue)
      onChange(newValue)
    } else {
      console.warn(
        '🗺️ CountrySelectInput: Invalid value attempted:',
        newValue,
        '- keeping current value:',
        value
      )
      // Don't update if invalid - keep the current valid selection
    }
  }

  return (
    <>
      <style>
        {`
          .ant-select-dropdown {
            z-index: 999999 !important;
          }
          .ant-select-selector {
            background: ${colors.SECONDARY_200} !important;
            border-radius: 8px !important;
          }
          .ant-select-selection-item {
            font-size: 14px !important;
            font-weight: 400 !important;
            color: ${colors.WHITE} !important;
          }
          .ant-select-dropdown .ant-select-item-option-content {
            font-size: 14px !important;
            font-weight: 400 !important;
            color: ${colors.WHITE} !important;
          }
        `}
      </style>
      <Text
        variant='paragraphSmall'
        weight='regular'
        color={colors.WHITE}
        style={`
          align-self: stretch;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          word-break: break-word;
        `}
      >
        {label}
        {required && (
          <span
            css={css`
              margin-left: 4px;
            `}
          >
            *
          </span>
        )}
      </Text>
      <Select
        placeholder={placeholder}
        allowClear={allowClear}
        disabled={disabled || loading}
        showSearch={showSearch}
        onChange={handleChange}
        options={countryOptions}
        value={value || undefined}
        tabIndex={tabIndex}
        filterOption={filterOption}
        optionFilterProp='label'
        loading={loading}
        mode={undefined}
        notFoundContent={
          loading ? (
            <div
              css={css`
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 8px;
              `}
            >
              <Spin size='small' />
            </div>
          ) : (
            'No countries found'
          )
        }
        css={css`
          ${paragraphMedium}
          ${weightRegular}
          font-size: 16px !important;
          color: ${colors.WHITE};
          width: 100%;
          background: ${colors.SECONDARY_200} !important;
          border-radius: 8px !important;
          height: 40px !important;
          ${disabled && 'opacity: 0.5 !important'}
          ${style}
        `}
        status={
          fieldErrors[name] || fieldErrors?.includes?.(name) ? 'error' : ''
        }
      />
      <ErrorLabel
        fieldErrors={fieldErrors || {}}
        customError={customError || {}}
        name={name}
      />
    </>
  )
}

export default CountrySelectInput
